import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Edit3, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const blank = { name: "", category: "", sort_order: 0, active: true };

export default function PublicMenuManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(blank);

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/public-menu-items"); setItems(data || []); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function startCreate(category = "") {
    const maxOrder = items.length ? Math.max(...items.map((i) => i.sort_order || 0)) : 0;
    setDraft({ ...blank, category, sort_order: maxOrder + 1 });
    setEditingId(null);
    setCreating(true);
  }
  function startEdit(it) { setDraft({ ...it }); setEditingId(it.id); setCreating(false); }
  function cancel() { setEditingId(null); setCreating(false); setDraft(blank); }

  async function save() {
    if (!draft.name?.trim()) return toast.error("Name is required");
    const payload = { ...draft, sort_order: Number(draft.sort_order) || 0 };
    try {
      if (editingId) { await api.put(`/public-menu-items/${editingId}`, payload); toast.success("Updated"); }
      else { await api.post("/public-menu-items", payload); toast.success("Added"); }
      cancel(); load();
    } catch { toast.error("Failed"); }
  }

  async function remove(it) {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try { await api.delete(`/public-menu-items/${it.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  }

  async function toggle(it) {
    try { await api.put(`/public-menu-items/${it.id}`, { active: !it.active }); load(); } catch {}
  }

  const grouped = items.reduce((acc, it) => {
    const key = it.category || "Other";
    (acc[key] = acc[key] || []).push(it);
    return acc;
  }, {});
  const categories = Object.keys(grouped);
  const inputCls = "w-full bg-[#0a0a0a] border border-white/10 px-3 py-2 text-sm focus:border-[#C9963F] focus:outline-none text-white";

  return (
    <div data-testid="public-menu-manager">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F] mb-2">Customer-facing · Shown on landing page</div>
          <h1 className="font-serif-brand text-4xl sm:text-5xl tracking-tight">Public Menu</h1>
          <p className="text-white/50 mt-2 text-sm">{items.length} items across {categories.length} categories. No prices shown to customers.</p>
        </div>
        <button onClick={() => startCreate()} data-testid="add-public-menu-item-btn"
          className="inline-flex items-center gap-2 bg-[#C9963F] text-black px-4 py-2 text-sm font-medium hover:bg-[#A67A2D]">
          <Plus size={16} /> Add Item
        </button>
      </div>

      {(creating || editingId) && (
        <div className="bg-[#0e0e0e] border border-[#C9963F]/40 p-6 mb-6 fade-up" data-testid="pmenu-editor">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F]">{editingId ? "Edit Item" : "New Item"}</div>
            <button onClick={cancel} className="text-white/50 hover:text-white"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Name *</label>
              <input className={inputCls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="pmenu-name" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Category</label>
              <input className={inputCls} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="CHAAT, BBQ, CURRY…" data-testid="pmenu-category" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Sort order</label>
              <input type="number" className={inputCls} value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
            </div>
            <div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} className="accent-[#C9963F]" />
                <span className="text-sm text-white/80">{draft.active ? "Active" : "Hidden"}</span>
              </label>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={save} data-testid="pmenu-save-btn" className="bg-[#C9963F] text-black font-medium px-5 py-2 text-sm hover:bg-[#A67A2D] inline-flex items-center gap-2">
              <Save size={14} /> Save
            </button>
            <button onClick={cancel} className="text-white/60 hover:text-white px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading && <div className="text-white/40 text-sm">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="bg-[#0e0e0e] border border-white/10 p-10 text-center text-white/40 text-sm">
          No menu items yet. Add your first one.
        </div>
      )}

      {categories.map((cat) => (
        <div key={cat} className="mb-6" data-testid={`pmenu-cat-${cat}`}>
          <div className="flex items-baseline gap-3 mb-2">
            <h2 className="text-[10px] uppercase tracking-[0.3em] text-[#C9963F]">{cat}</h2>
            <span className="text-[10px] uppercase tracking-wider text-white/40">{grouped[cat].length}</span>
            <button onClick={() => startCreate(cat)} className="ml-auto text-[10px] uppercase tracking-wider text-white/60 hover:text-[#C9963F] inline-flex items-center gap-1">
              <Plus size={11} /> Add to {cat}
            </button>
          </div>
          <div className="bg-[#0e0e0e] border border-white/10">
            {grouped[cat].map((it) => (
              <div key={it.id} data-testid={`pmenu-item-${it.id}`}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5 last:border-b-0 ${!it.active ? "opacity-40" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white">{it.name}</div>
                  {!it.active && <div className="text-[10px] uppercase tracking-wider text-white/40">Hidden from customers</div>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggle(it)} title={it.active ? "Hide" : "Show"} className="p-1.5 text-white/60 hover:text-[#C9963F]">
                    {it.active ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  <button onClick={() => startEdit(it)} title="Edit" data-testid={`pmenu-edit-${it.id}`} className="p-1.5 text-white/60 hover:text-[#C9963F]">
                    <Edit3 size={14} />
                  </button>
                  <button onClick={() => remove(it)} title="Delete" data-testid={`pmenu-delete-${it.id}`} className="p-1.5 text-white/60 hover:text-red-400">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
